import React, { useState, useEffect, useRef, useCallback } from "react";
import { Pencil, Palette, Trash2, X } from "lucide-react";

// ── Types ────────────────────────────────────────────────────────────────────

interface VisualEditorProps {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** The div that wraps the iframe — overlay positions relative to this */
  containerRef: React.RefObject<HTMLDivElement | null>;
  editMode: boolean;
  onSendEdit: (message: string) => void;
  /** Incremented on preview reload — triggers re-injection of listeners */
  previewKey: number;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface SelectedElement {
  el: HTMLElement;
  desc: string;
  rect: Rect;
}

// ── Preset colors ────────────────────────────────────────────────────────────

const PRESET_COLORS = [
  { hex: "#6366f1", name: "Indigo" },
  { hex: "#8b5cf6", name: "Violet" },
  { hex: "#ec4899", name: "Pink" },
  { hex: "#ef4444", name: "Red" },
  { hex: "#f97316", name: "Orange" },
  { hex: "#eab308", name: "Yellow" },
  { hex: "#22c55e", name: "Green" },
  { hex: "#06b6d4", name: "Cyan" },
];

// ── Smart element description ────────────────────────────────────────────────

function describeElement(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  const cls = (typeof el.className === "string" ? el.className : "").toLowerCase();
  const text = (el.textContent || "").trim().slice(0, 50);
  const shortText = text.length > 40 ? text.slice(0, 37) + "..." : text;

  // Context from ancestors
  const heroAncestor = el.closest('[class*="hero"]') || el.closest("section:first-of-type");
  const navAncestor = el.closest("nav") || el.closest('[class*="nav"]');
  const footerAncestor = el.closest("footer") || el.closest('[class*="footer"]');
  const cardAncestor = el.closest('[class*="card"]');
  const pricingAncestor = el.closest('[class*="pric"]');
  const featureAncestor = el.closest('[class*="feature"]');
  const headerAncestor = el.closest("header") || el.closest('[class*="header"]');

  let ctx = "";
  if (pricingAncestor) ctx = "pricing ";
  else if (heroAncestor) ctx = "hero ";
  else if (navAncestor) ctx = "navigation ";
  else if (footerAncestor) ctx = "footer ";
  else if (featureAncestor) ctx = "feature ";
  else if (cardAncestor) ctx = "card ";
  else if (headerAncestor) ctx = "header ";

  // CTA detection
  const isCta =
    cls.includes("cta") ||
    cls.includes("primary") ||
    (tag === "button" && heroAncestor != null) ||
    (tag === "a" && heroAncestor != null && cls.includes("bg-"));

  if (tag === "h1") return `the ${ctx}headline "${shortText}"`;
  if (tag === "h2") return `the ${ctx}section heading "${shortText}"`;
  if (tag === "h3" || tag === "h4") return `the ${ctx}subheading "${shortText}"`;
  if (tag === "button" || el.getAttribute("role") === "button") {
    if (isCta) return `the ${ctx}CTA button "${shortText}"`;
    return `the ${ctx}button "${shortText}"`;
  }
  if (tag === "a") {
    if (isCta) return `the ${ctx}CTA link "${shortText}"`;
    return `the ${ctx}link "${shortText}"`;
  }
  if (tag === "p") return `the ${ctx}paragraph "${shortText}"`;
  if (tag === "span") return `the ${ctx}text "${shortText}"`;
  if (tag === "img") {
    const alt = el.getAttribute("alt");
    return `the ${ctx}image${alt ? ` "${alt}"` : ""}`;
  }
  if (tag === "li") return `the ${ctx}list item "${shortText}"`;
  if (tag === "input" || tag === "textarea") {
    const placeholder = el.getAttribute("placeholder");
    return `the ${ctx}${tag} field${placeholder ? ` "${placeholder}"` : ""}`;
  }
  if (tag === "section" || tag === "div") {
    if (ctx) return `the ${ctx}section`;
    if (shortText.length <= 30 && shortText.length > 0) return `the "${shortText}" section`;
    return `the ${tag} container`;
  }
  return `the ${ctx}${tag} element${shortText ? ` "${shortText}"` : ""}`;
}

// ── Iframe traversal helpers ─────────────────────────────────────────────────

function getInnerDocument(outerIframe: HTMLIFrameElement): Document | null {
  try {
    const outerDoc = outerIframe.contentDocument;
    if (!outerDoc) return null;
    const innerIframe = outerDoc.querySelector("iframe") as HTMLIFrameElement;
    return innerIframe?.contentDocument ?? null;
  } catch {
    return null;
  }
}

function getIframeOffset(
  outerIframe: HTMLIFrameElement,
  container: HTMLElement
): { top: number; left: number } {
  const cRect = container.getBoundingClientRect();
  const iRect = outerIframe.getBoundingClientRect();
  return { top: iRect.top - cRect.top, left: iRect.left - cRect.left };
}

// ── Toolbar button ───────────────────────────────────────────────────────────

function ToolbarBtn({
  icon,
  label,
  onClick,
  active,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        padding: "5px 10px",
        background: active ? "rgba(99,102,241,0.2)" : "transparent",
        border: "none",
        borderRadius: 5,
        color: danger ? "#f87171" : "#fff",
        cursor: "pointer",
        fontSize: 13,
        fontFamily: "system-ui, sans-serif",
        whiteSpace: "nowrap",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = danger
          ? "rgba(248,113,113,0.15)"
          : "rgba(99,102,241,0.2)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = active
          ? "rgba(99,102,241,0.2)"
          : "transparent";
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function VisualEditor({
  iframeRef,
  containerRef,
  editMode,
  onSendEdit,
  previewKey,
}: VisualEditorProps) {
  const [selectedEl, setSelectedEl] = useState<SelectedElement | null>(null);
  const [showColors, setShowColors] = useState(false);
  const [customHex, setCustomHex] = useState("");

  const isEditingTextRef = useRef(false);
  const prevHighlightRef = useRef<HTMLElement | null>(null);
  const injectedDocRef = useRef<Document | null>(null);

  const clearSelection = useCallback(() => {
    setSelectedEl(null);
    setShowColors(false);
    setCustomHex("");
    isEditingTextRef.current = false;
  }, []);

  // Translate element rect from inner iframe to container-relative coords
  const toContainerRect = useCallback(
    (elRect: DOMRect): Rect | null => {
      const outer = iframeRef.current;
      const container = containerRef.current;
      if (!outer || !container) return null;
      const offset = getIframeOffset(outer, container);
      return {
        top: offset.top + elRect.top,
        left: offset.left + elRect.left,
        width: elRect.width,
        height: elRect.height,
      };
    },
    [iframeRef, containerRef]
  );

  // ── Inject / cleanup listeners into inner iframe ───────────────────────────

  useEffect(() => {
    if (!editMode) {
      clearSelection();
      // Clean up any previous injection
      if (injectedDocRef.current) {
        try {
          injectedDocRef.current.body.style.cursor = "";
        } catch {}
        injectedDocRef.current = null;
      }
      return;
    }

    const outerIframe = iframeRef.current;
    if (!outerIframe) return;

    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout>;

    function handleMouseOver(e: Event) {
      const el = e.target as HTMLElement;
      if (!el || el === injectedDocRef.current?.body || el === injectedDocRef.current?.documentElement) return;
      if (isEditingTextRef.current) return;

      if (prevHighlightRef.current && prevHighlightRef.current !== el) {
        prevHighlightRef.current.style.outline = "";
        prevHighlightRef.current.style.outlineOffset = "";
      }

      el.style.outline = "2px solid rgba(99, 102, 241, 0.5)";
      el.style.outlineOffset = "2px";
      prevHighlightRef.current = el;
    }

    function handleMouseOut(e: Event) {
      const el = e.target as HTMLElement;
      if (!el) return;
      el.style.outline = "";
      el.style.outlineOffset = "";
      if (prevHighlightRef.current === el) prevHighlightRef.current = null;
    }

    function handleClick(e: Event) {
      if (isEditingTextRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const el = e.target as HTMLElement;
      if (!el || el === injectedDocRef.current?.body || el === injectedDocRef.current?.documentElement) return;

      const rect = el.getBoundingClientRect();
      const cRect = toContainerRect(rect);
      if (!cRect) return;

      const desc = describeElement(el);
      setSelectedEl({ el, desc, rect: cRect });
      setShowColors(false);
    }

    function inject() {
      if (disposed) return;
      const doc = getInnerDocument(outerIframe!);
      if (!doc || !doc.body) {
        retryTimer = setTimeout(inject, 500);
        return;
      }

      injectedDocRef.current = doc;
      doc.body.style.cursor = "crosshair";
      doc.addEventListener("mouseover", handleMouseOver, true);
      doc.addEventListener("mouseout", handleMouseOut, true);
      doc.addEventListener("click", handleClick, true);
    }

    // Wait a tick for iframe to load after previewKey change
    retryTimer = setTimeout(inject, 300);

    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      const doc = injectedDocRef.current;
      if (doc) {
        doc.removeEventListener("mouseover", handleMouseOver, true);
        doc.removeEventListener("mouseout", handleMouseOut, true);
        doc.removeEventListener("click", handleClick, true);
        try {
          doc.body.style.cursor = "";
        } catch {}
      }
      if (prevHighlightRef.current) {
        try {
          prevHighlightRef.current.style.outline = "";
          prevHighlightRef.current.style.outlineOffset = "";
        } catch {}
        prevHighlightRef.current = null;
      }
      injectedDocRef.current = null;
      clearSelection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, previewKey]);

  // Click outside container dismisses toolbar
  useEffect(() => {
    if (!selectedEl) return;
    function handleOutside(e: MouseEvent) {
      const container = containerRef.current;
      if (container && !container.contains(e.target as Node)) {
        clearSelection();
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [selectedEl, containerRef, clearSelection]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleEditText = useCallback(() => {
    if (!selectedEl) return;
    const { el, desc } = selectedEl;

    isEditingTextRef.current = true;
    const originalText = el.textContent || "";
    el.contentEditable = "true";
    el.style.outline = "2px solid #6366f1";
    el.style.outlineOffset = "2px";
    el.focus();

    // Select all text
    try {
      const range = el.ownerDocument.createRange();
      range.selectNodeContents(el);
      const sel = el.ownerDocument.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    } catch {}

    function finish() {
      el.contentEditable = "false";
      el.style.outline = "";
      el.style.outlineOffset = "";
      isEditingTextRef.current = false;

      const newText = (el.textContent || "").trim();
      if (newText && newText !== originalText.trim()) {
        onSendEdit(`Change ${desc} text to "${newText}"`);
      }

      el.removeEventListener("blur", finish);
      el.removeEventListener("keydown", handleKey);
      clearSelection();
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        finish();
      }
      if (e.key === "Escape") {
        el.textContent = originalText;
        el.contentEditable = "false";
        el.style.outline = "";
        el.style.outlineOffset = "";
        isEditingTextRef.current = false;
        el.removeEventListener("blur", finish);
        el.removeEventListener("keydown", handleKey);
        clearSelection();
      }
    }

    el.addEventListener("blur", finish);
    el.addEventListener("keydown", handleKey);
    setSelectedEl(null); // hide toolbar while editing
  }, [selectedEl, onSendEdit, clearSelection]);

  const handleChangeColor = useCallback(
    (color: string) => {
      if (!selectedEl) return;
      onSendEdit(`Change ${selectedEl.desc} color to ${color}`);
      clearSelection();
    },
    [selectedEl, onSendEdit, clearSelection]
  );

  const handleDelete = useCallback(() => {
    if (!selectedEl) return;
    onSendEdit(`Remove the ${selectedEl.desc} element`);
    clearSelection();
  }, [selectedEl, onSendEdit, clearSelection]);

  if (!editMode) return null;

  // ── Toolbar positioning ────────────────────────────────────────────────────

  const TOOLBAR_H = 44;
  const containerW = containerRef.current?.offsetWidth ?? 800;

  const toolbarY = selectedEl
    ? selectedEl.rect.top > TOOLBAR_H + 8
      ? selectedEl.rect.top - TOOLBAR_H - 8
      : selectedEl.rect.top + selectedEl.rect.height + 8
    : 0;

  const toolbarX = selectedEl
    ? Math.max(8, Math.min(selectedEl.rect.left + selectedEl.rect.width / 2 - 130, containerW - 280))
    : 0;

  // Color picker goes below toolbar
  const colorPickerBelow = selectedEl ? selectedEl.rect.top > TOOLBAR_H + 8 : true;
  const colorPickerY = colorPickerBelow ? toolbarY + TOOLBAR_H + 4 : toolbarY - 140;

  return (
    <>
      {/* Selection highlight */}
      {selectedEl && (
        <div
          style={{
            position: "absolute",
            top: selectedEl.rect.top,
            left: selectedEl.rect.left,
            width: selectedEl.rect.width,
            height: selectedEl.rect.height,
            border: "2px solid #6366f1",
            borderRadius: 4,
            pointerEvents: "none",
            zIndex: 40,
            transition: "all 0.15s ease",
          }}
        />
      )}

      {/* Floating toolbar */}
      {selectedEl && (
        <div
          style={{
            position: "absolute",
            top: toolbarY,
            left: toolbarX,
            zIndex: 50,
            background: "#141414",
            border: "1px solid #6366f1",
            borderRadius: 8,
            padding: "4px 6px",
            display: "flex",
            alignItems: "center",
            gap: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          }}
        >
          <ToolbarBtn
            icon={<Pencil size={14} />}
            label="Edit Text"
            onClick={handleEditText}
          />
          <div style={{ width: 1, height: 20, background: "#333", margin: "0 2px" }} />
          <ToolbarBtn
            icon={<Palette size={14} />}
            label="Color"
            onClick={() => setShowColors(!showColors)}
            active={showColors}
          />
          <div style={{ width: 1, height: 20, background: "#333", margin: "0 2px" }} />
          <ToolbarBtn
            icon={<Trash2 size={14} />}
            label="Delete"
            onClick={handleDelete}
            danger
          />
          <button
            onClick={clearSelection}
            title="Close"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              marginLeft: 2,
              background: "transparent",
              border: "none",
              color: "#666",
              cursor: "pointer",
              borderRadius: 4,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.color = "#666";
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Color picker popover */}
      {selectedEl && showColors && (
        <div
          style={{
            position: "absolute",
            top: colorPickerY,
            left: toolbarX,
            zIndex: 51,
            background: "#141414",
            border: "1px solid #6366f1",
            borderRadius: 8,
            padding: 12,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            minWidth: 220,
          }}
        >
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {PRESET_COLORS.map((c) => (
              <button
                key={c.hex}
                onClick={() => handleChangeColor(c.hex)}
                title={c.name}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: c.hex,
                  border: "2px solid rgba(255,255,255,0.12)",
                  cursor: "pointer",
                  transition: "transform 0.1s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.transform = "scale(1.15)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.transform = "scale(1)";
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <input
              type="text"
              placeholder="#hex"
              value={customHex}
              onChange={(e) => setCustomHex(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && customHex.match(/^#[0-9a-fA-F]{3,8}$/)) {
                  handleChangeColor(customHex);
                }
              }}
              style={{
                flex: 1,
                background: "#1a1a2e",
                border: "1px solid #333",
                borderRadius: 4,
                color: "#fff",
                padding: "5px 8px",
                fontSize: 13,
                fontFamily: "system-ui, monospace",
                outline: "none",
              }}
            />
            <button
              onClick={() => {
                if (customHex.match(/^#[0-9a-fA-F]{3,8}$/)) {
                  handleChangeColor(customHex);
                }
              }}
              style={{
                background: customHex.match(/^#[0-9a-fA-F]{3,8}$/) ? "#6366f1" : "#333",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "5px 12px",
                cursor: customHex.match(/^#[0-9a-fA-F]{3,8}$/) ? "pointer" : "default",
                fontSize: 13,
                fontFamily: "system-ui, sans-serif",
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </>
  );
}
